import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Settings,
  Save,
  Refresh,
  Build,
  PhoneIphone,
  AndroidOutlined,
  Instagram,
  Twitter,
  LinkedIn,
  YouTube,
  MusicNote,
  Apple,
  Shop,
} from '@mui/icons-material';
import adminService from '../../../services/admin.service';

interface SocialMediaLinks {
  instagram: string;
  twitter: string;
  linkedin: string;
  youtube: string;
  tiktok: string;
}

interface AppConfigData {
  _id?: string;
  appVersion: string;
  appBuildNumberIOS: string;
  appBuildNumberAndroid: string;
  appMaintenanceMode: boolean;
  appMaintenanceMessage: string;
  appMaintenanceEstimatedTime: number;
  appSocialMediaLinks: SocialMediaLinks;
  storeUrlIOS: string;
  storeUrlAndroid: string;
  createdAt?: string;
  updatedAt?: string;
}

const AppConfig: React.FC = () => {
  const [config, setConfig] = useState<AppConfigData>({
    appVersion: '',
    appBuildNumberIOS: '',
    appBuildNumberAndroid: '',
    appMaintenanceMode: false,
    appMaintenanceMessage: '',
    appMaintenanceEstimatedTime: 0,
    appSocialMediaLinks: {
      instagram: '',
      twitter: '',
      linkedin: '',
      youtube: '',
      tiktok: '',
    },
    storeUrlIOS: '',
    storeUrlAndroid: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminService.getInitialConfig();
      if (response.status) {
        setConfig(response.data?.initialConfig || config);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ayarlar yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConfig = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await adminService.updateAppConfig(config);
      setSuccess('Uygulama ayarları başarıyla güncellendi!');
      setHasChanges(false);
      await fetchConfig();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ayarlar güncellenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSocialMedia = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await adminService.updateSocialMediaLinks(config.appSocialMediaLinks);
      setSuccess('Sosyal medya linkleri başarıyla güncellendi!');
      setHasChanges(false);
      await fetchConfig();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Sosyal medya linkleri güncellenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStoreUrls = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await adminService.updateStoreUrls({
        storeUrlIOS: config.storeUrlIOS,
        storeUrlAndroid: config.storeUrlAndroid,
      });
      setSuccess('Mağaza URL\'leri başarıyla güncellendi!');
      setHasChanges(false);
      await fetchConfig();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Mağaza URL\'leri güncellenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: keyof AppConfigData, value: any) => {
    setConfig({ ...config, [field]: value });
    setHasChanges(true);
  };

  const handleSocialMediaChange = (platform: keyof SocialMediaLinks, value: string) => {
    setConfig({
      ...config,
      appSocialMediaLinks: {
        ...config.appSocialMediaLinks,
        [platform]: value,
      },
    });
    setHasChanges(true);
  };

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours} saat ${minutes} dakika`;
    }
    return `${minutes} dakika`;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Uygulama Ayarları
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Mobil uygulama konfigürasyonunu yönetin.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchConfig}
            disabled={loading}
          >
            Yenile
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleUpdateConfig}
            disabled={loading || !hasChanges}
          >
            {loading ? <CircularProgress size={24} /> : 'Kaydet'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {hasChanges && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Kaydedilmemiş değişiklikleriniz var.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Versiyon Bilgileri */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Build color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Versiyon Bilgileri
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Uygulama Versiyonu"
                  value={config.appVersion}
                  onChange={(e) => handleFieldChange('appVersion', e.target.value)}
                  placeholder="1.0.0"
                  helperText="Örn: 1.0.0, 2.1.3"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="iOS Build Numarası"
                  value={config.appBuildNumberIOS}
                  onChange={(e) => handleFieldChange('appBuildNumberIOS', e.target.value)}
                  placeholder="1"
                  InputProps={{
                    startAdornment: <PhoneIphone sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Android Build Numarası"
                  value={config.appBuildNumberAndroid}
                  onChange={(e) => handleFieldChange('appBuildNumberAndroid', e.target.value)}
                  placeholder="1"
                  InputProps={{
                    startAdornment: <AndroidOutlined sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Bakım Modu */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Settings color="warning" />
              <Typography variant="h6" fontWeight="bold">
                Bakım Modu
              </Typography>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={config.appMaintenanceMode}
                  onChange={(e) => handleFieldChange('appMaintenanceMode', e.target.checked)}
                  color="warning"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="600">
                    Bakım Modunu Aktif Et
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Aktif olduğunda kullanıcılar uygulamayı kullanamazlar
                  </Typography>
                </Box>
              }
              sx={{ mb: 3 }}
            />

            {config.appMaintenanceMode && (
              <>
                <Alert severity="warning" sx={{ mb: 3 }}>
                  ⚠️ Bakım modu aktif! Kullanıcılar uygulamaya erişemeyecek.
                </Alert>

                <TextField
                  fullWidth
                  label="Bakım Mesajı"
                  value={config.appMaintenanceMessage}
                  onChange={(e) => handleFieldChange('appMaintenanceMessage', e.target.value)}
                  multiline
                  rows={3}
                  placeholder="Sistem güncellemesi yapılıyor. Lütfen bekleyiniz..."
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label="Tahmini Bakım Süresi (saniye)"
                  type="number"
                  value={config.appMaintenanceEstimatedTime}
                  onChange={(e) =>
                    handleFieldChange('appMaintenanceEstimatedTime', parseInt(e.target.value) || 0)
                  }
                  helperText={
                    config.appMaintenanceEstimatedTime > 0
                      ? `Yaklaşık ${formatTime(config.appMaintenanceEstimatedTime)}`
                      : '0 = Süre belirtilmedi'
                  }
                  placeholder="3600"
                />
              </>
            )}
          </Paper>
        </Grid>

        {/* Sosyal Medya Linkleri */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Instagram color="error" />
                <Typography variant="h6" fontWeight="bold">
                  Sosyal Medya Linkleri
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<Save />}
                onClick={handleUpdateSocialMedia}
                disabled={loading}
              >
                Kaydet
              </Button>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Instagram"
                  value={config.appSocialMediaLinks.instagram}
                  onChange={(e) => handleSocialMediaChange('instagram', e.target.value)}
                  placeholder="https://www.instagram.com/unimate"
                  InputProps={{
                    startAdornment: <Instagram sx={{ mr: 1, color: '#E4405F' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Twitter"
                  value={config.appSocialMediaLinks.twitter}
                  onChange={(e) => handleSocialMediaChange('twitter', e.target.value)}
                  placeholder="https://www.twitter.com/unimate"
                  InputProps={{
                    startAdornment: <Twitter sx={{ mr: 1, color: '#1DA1F2' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="LinkedIn"
                  value={config.appSocialMediaLinks.linkedin}
                  onChange={(e) => handleSocialMediaChange('linkedin', e.target.value)}
                  placeholder="https://www.linkedin.com/company/unimate"
                  InputProps={{
                    startAdornment: <LinkedIn sx={{ mr: 1, color: '#0A66C2' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="YouTube"
                  value={config.appSocialMediaLinks.youtube}
                  onChange={(e) => handleSocialMediaChange('youtube', e.target.value)}
                  placeholder="https://www.youtube.com/@unimate"
                  InputProps={{
                    startAdornment: <YouTube sx={{ mr: 1, color: '#FF0000' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="TikTok"
                  value={config.appSocialMediaLinks.tiktok}
                  onChange={(e) => handleSocialMediaChange('tiktok', e.target.value)}
                  placeholder="https://www.tiktok.com/@unimate"
                  InputProps={{
                    startAdornment: <MusicNote sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Mağaza URL'leri */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Shop color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Mağaza URL'leri
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<Save />}
                onClick={handleUpdateStoreUrls}
                disabled={loading}
              >
                Kaydet
              </Button>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="App Store URL"
                  value={config.storeUrlIOS}
                  onChange={(e) => handleFieldChange('storeUrlIOS', e.target.value)}
                  placeholder="https://apps.apple.com/tr/app/unimate"
                  InputProps={{
                    startAdornment: <Apple sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                  helperText="iOS uygulama indirme linki"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Google Play URL"
                  value={config.storeUrlAndroid}
                  onChange={(e) => handleFieldChange('storeUrlAndroid', e.target.value)}
                  placeholder="https://play.google.com/store/apps/details?id=com.unimate"
                  InputProps={{
                    startAdornment: <AndroidOutlined sx={{ mr: 1, color: '#3DDC84' }} />,
                  }}
                  helperText="Android uygulama indirme linki"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Özet Bilgiler */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, border: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Mevcut Konfigürasyon Özeti
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Uygulama Versiyonu
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {config.appVersion || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Build (iOS / Android)
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {config.appBuildNumberIOS || '-'} / {config.appBuildNumberAndroid || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Bakım Modu
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="600"
                    color={config.appMaintenanceMode ? 'error.main' : 'success.main'}
                  >
                    {config.appMaintenanceMode ? '🔴 Aktif' : '🟢 Pasif'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Son Güncelleme
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {config.updatedAt
                      ? new Date(config.updatedAt).toLocaleString('tr-TR')
                      : '-'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Yardım Notları */}
      <Paper
        elevation={0}
        sx={{ p: 3, mt: 4, borderRadius: 3, border: '1px solid #e3f2fd', backgroundColor: '#f0f7ff' }}
      >
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          📝 Notlar
        </Typography>
        <Box component="ul" sx={{ pl: 2, m: 0 }}>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            Versiyon formatı: <strong>X.Y.Z</strong> (Örn: 1.0.0, 2.1.3)
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            Build numaraları her yeni derleme için artırılmalıdır
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            Bakım modu aktif olduğunda tüm kullanıcılar uygulamadan çıkarılır
          </Typography>
          <Typography component="li" variant="body2">
            Tahmini bakım süresi saniye cinsinden girilmelidir (1 saat = 3600 saniye)
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default AppConfig;

